const TRANSITION_STORAGE_KEY = "kinioto-character-transition";
const ROTATION_STEP = 50;
const SCROLL_ROTATION_DURATION = 1500;
const SCROLL_GESTURE_END_DELAY = 150;

// CSSのcubic-bezier(0.22, 1, 0.36, 1)と同じ曲線を使用する。
function easeCdRotation(progress) {
    if (progress <= 0) return 0;
    if (progress >= 1) return 1;

    let lower = 0;
    let upper = 1;

    // 経過時間（X）に対応する曲線上の位置を求める。
    for (let i = 0; i < 20; i++) {
        const t = (lower + upper) / 2;
        const inverse = 1 - t;
        const x = 3 * inverse * inverse * t * 0.22 + 3 * inverse * t * t * 0.36 + t * t * t;

        if (x < progress) lower = t;
        else upper = t;
    }

    const t = (lower + upper) / 2;
    return 1 - Math.pow(1 - t, 3);
}

class Character {
    constructor() {
        this.paginationButtons = document.querySelectorAll(".js-character-pagenation-btn");
        this.characterMain = document.querySelector(".js-character-main");
        this.characterSwitchButton = document.querySelector(".js-character-main-switch");
        this.characterLooksImages = document.querySelectorAll(".js-character-looks-image");
        const storedRotation = Number.parseFloat(document.body.dataset.characterRotation);
        this.transitionRotation = Number.isFinite(storedRotation) ? storedRotation : ROTATION_STEP;
        this.scrollRotation = 0;
        this.renderedScrollRotation = 0;
        this.lastScrollTop = this.getScrollTop();
        this.isScrollGestureActive = false;
        this.scrollEndTimer = null;
        this.rotationRafId = null;
        this.navigationLock = null;
        this.eventBind();
        this.initScrollRotation();
    }

    eventBind() {
        this.paginationButtons.forEach((button) => {
            button.addEventListener("click", (event) => this.saveTransition(event, button));
        });

        window.addEventListener("pageshow", () => this.releaseNavigationLock());

        if (this.characterMain && this.characterSwitchButton) {
            this.characterSwitchButton.addEventListener("click", () => this.switchCharacterImage());
        }
    }

    switchCharacterImage() {
        const nextImage = this.characterMain.dataset.img === "2" ? "1" : "2";

        this.characterMain.dataset.img = nextImage;
        this.characterLooksImages.forEach((image) => {
            image.src = nextImage === "2" ? image.dataset.alternateSrc : image.dataset.primarySrc;
        });
        this.characterSwitchButton.setAttribute("aria-pressed", String(nextImage === "2"));
        this.spinCd();
    }

    initScrollRotation() {
        window.addEventListener("scroll", () => this.handleScroll(), { passive: true });
        this.setScrollRotation(0);
    }

    getScrollTop() {
        const documentElement = document.documentElement;
        const maxScroll = Math.max(documentElement.scrollHeight - window.innerHeight, 0);
        return Math.min(Math.max(window.scrollY || documentElement.scrollTop, 0), maxScroll);
    }

    handleScroll() {
        const scrollTop = this.getScrollTop();
        const hasScrolled = scrollTop !== this.lastScrollTop;

        this.lastScrollTop = scrollTop;

        if (!hasScrolled) return;

        if (!this.isScrollGestureActive) {
            this.isScrollGestureActive = true;
            this.spinCd();
        }

        clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = setTimeout(() => {
            this.isScrollGestureActive = false;
        }, SCROLL_GESTURE_END_DELAY);
    }

    spinCd() {
        const fromRotation = this.renderedScrollRotation;
        const toRotation = this.scrollRotation + ROTATION_STEP;
        let startTime = null;

        this.scrollRotation = toRotation;
        if (this.rotationRafId !== null) cancelAnimationFrame(this.rotationRafId);

        const render = (time) => {
            if (startTime === null) startTime = time;

            const progress = Math.min((time - startTime) / SCROLL_ROTATION_DURATION, 1);
            const easedProgress = easeCdRotation(progress);
            this.renderedScrollRotation = fromRotation + (toRotation - fromRotation) * easedProgress;
            this.setScrollRotation(this.renderedScrollRotation);

            if (progress < 1) {
                this.rotationRafId = requestAnimationFrame(render);
            } else {
                this.rotationRafId = null;
            }
        };

        this.rotationRafId = requestAnimationFrame(render);
    }

    setScrollRotation(rotation) {
        document.body.style.setProperty("--cd-scroll-rotation", `${rotation}deg`);

        const currentRotation = this.transitionRotation + rotation;
        document.body.dataset.characterRotation = String(currentRotation);
        document.body.dataset.characterRotationCount = String(currentRotation / ROTATION_STEP);
    }

    saveTransition(event, button) {
        // 新しいタブで開く操作では、現在のタブの次回表示状態を変更しない。
        if (
            event.defaultPrevented ||
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            button.target === "_blank" ||
            button.hasAttribute("download")
        ) {
            return;
        }

        const direction = button.classList.contains("-prev") ? "prev" : "next";
        const storedRotation = Number.parseFloat(document.body.dataset.characterRotation);
        const currentRotation = Number.isFinite(storedRotation) ? storedRotation : ROTATION_STEP;
        const toRotation = currentRotation + (direction === "next" ? ROTATION_STEP : -ROTATION_STEP);
        const targetUrl = new URL(button.href, window.location.href);

        try {
            sessionStorage.setItem(
                TRANSITION_STORAGE_KEY,
                JSON.stringify({
                    direction,
                    fromRotation: currentRotation,
                    toRotation,
                    targetPath: targetUrl.pathname,
                })
            );
        } catch (error) {
            // Storageが利用できない場合も通常のリンク遷移は継続する。
        }

        // スマートフォン・タブレット版Chromeで遷移開始時に旧ページが一瞬スクロールする描画を回避する。
        const userAgent = navigator.userAgent;
        const isIPad =
            /iPad/.test(userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
        const isMobileDevice = /Android|iPhone|iPod/.test(userAgent) || isIPad;
        const isChrome =
            /Chrome\/|CriOS\//.test(userAgent) &&
            !/EdgA?\/|EdgiOS\/|OPR\/|OPiOS\/|SamsungBrowser\//.test(userAgent);
        const isMobileChrome = isMobileDevice && isChrome;

        if (isMobileChrome) {
            event.preventDefault();
            this.lockNavigationFrame();

            // 固定した旧ページを一度描画してから遷移を開始する。
            requestAnimationFrame(() => {
                requestAnimationFrame(() => window.location.assign(targetUrl.href));
            });
        }
    }

    lockNavigationFrame() {
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        const { style } = document.body;

        this.navigationLock = {
            scrollY,
            position: style.position,
            top: style.top,
            left: style.left,
            width: style.width,
        };

        style.position = "fixed";
        style.top = `-${scrollY}px`;
        style.left = "0";
        style.width = "100%";
    }

    releaseNavigationLock() {
        if (!this.navigationLock) return;

        const { scrollY, position, top, left, width } = this.navigationLock;
        const { style } = document.body;

        style.position = position;
        style.top = top;
        style.left = left;
        style.width = width;
        this.navigationLock = null;
        window.scrollTo(0, scrollY);
    }
}

new Character();
